import React, { useState, useRef, useEffect } from 'react';
import styles from '../styles.module.scss';
import { ReactComponent as ChevronDownIcon } from '../../../../shared/icons/chevron-down.svg';

interface SyncOption {
  value: string;
  label: string;
  subLabel?: string;
}

interface SyncDropdownProps {
  value: string;
  options: SyncOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
}

const SyncDropdown: React.FC<SyncDropdownProps> = ({
  value,
  options,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Find the currently selected option
  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className={styles.syncDropdownContainer} ref={dropdownRef}>
      <button
        type="button"
        className={`${styles.syncDropdownTrigger} ${isOpen ? styles.open : ''} ${disabled ? styles.disabled : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span className={styles.syncDropdownValue}>
          {selectedOption?.label || 'Select...'}
        </span>
        <ChevronDownIcon className={`${styles.syncDropdownChevron} ${isOpen ? styles.rotated : ''}`} />
      </button>

      {isOpen && (
        <div className={styles.syncDropdownMenu}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`${styles.syncDropdownOption} ${option.value === value ? styles.selected : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              <span className={styles.syncDropdownOptionLabel}>{option.label}</span>
              {option.subLabel && (
                <span className={styles.syncDropdownOptionSub}>{option.subLabel}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SyncDropdown;
